import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { fetchAuthBlob } from '@utils/blobUrl';
import { isTrustedApiUrl } from '@utils/apiOrigin';
import type { AttachmentSource, PreviewableAttachment } from './types';
import { UnavailableSlide } from './AttachmentTray.styled';

const resolveObjectUrl = async (source: AttachmentSource | null): Promise<string | null> => {
  if (!source) return null;
  if (source.kind === 'local') return URL.createObjectURL(source.file);
  if (!isTrustedApiUrl(source.url)) return null;
  try {
    return URL.createObjectURL(await fetchAuthBlob(source.url));
  } catch {
    return null;
  }
};

interface SlideSources {
  /** Snapshot the URLs were resolved from; index-aligned with `urls`. */
  captured: PreviewableAttachment[];
  urls: (string | null)[];
}

/**
 * Resolve every slide up front. The lightbox only mounts on an explicit click
 * over one message's images, so the set is small and bounded by the tray's
 * size cap; the carousel needs them all anyway to allow arrow navigation.
 */
const useSlideUrls = (items: PreviewableAttachment[]): SlideSources => {
  // Captured once: the item set must not shift while the user is browsing it.
  const [captured] = useState(() => items);
  const [urls, setUrls] = useState<(string | null)[]>(() => captured.map(() => null));

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];

    // The reduced copy where there is one: it is the same picture at the size a
    // screen can show, and it is what the model was given.
    void Promise.all(
      captured.map((item) => resolveObjectUrl(item.preview?.source ?? item.source)),
    ).then((resolved) => {
      resolved.forEach((url) => url && created.push(url));
      if (cancelled) {
        created.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      setUrls(resolved);
    });

    return () => {
      cancelled = true;
      created.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [captured]);

  return { captured, urls };
};

interface ImageLightboxProps {
  items: PreviewableAttachment[];
  startIndex: number;
  onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ items, startIndex, onClose }) => {
  const intl = useIntl();
  const [index, setIndex] = useState(startIndex);
  const { captured, urls } = useSlideUrls(items);

  return (
    <Lightbox
      open
      close={onClose}
      index={index}
      on={{ view: ({ index: viewed }) => setIndex(viewed) }}
      slides={urls.map((url, i) => ({ src: url ?? '', alt: captured[i]?.filename }))}
      plugins={[Zoom]}
      carousel={{ finite: true }}
      render={{
        ...(captured.length > 1 ? {} : { buttonPrev: () => null, buttonNext: () => null }),
        // An unresolvable slide would otherwise render as a broken image with
        // no explanation. Returning undefined defers to the default renderer.
        slide: ({ slide }) =>
          slide.src ? undefined : (
            <UnavailableSlide>
              {intl.formatMessage({
                id: 'attachments.previewUnavailable',
                defaultMessage: 'Preview unavailable',
              })}
            </UnavailableSlide>
          ),
      }}
    />
  );
};

export default ImageLightbox;
